import { useCallback, useState } from 'react'

import { useAppToast } from './useAppToast'

export interface IDownload {
  /** Liga o `isLoading` do botão enquanto o arquivo está sendo gerado. */
  isDownloading: boolean
  /**
   * Roda o download e cuida do estado e da recusa.
   *
   * @param run Chamada do service (`() => service.downloadParticipantsXlsx(id)`).
   * @param errorTitle Título do toast de erro. A descrição sai do erro da API.
   */
  download: (run: () => Promise<unknown>, errorTitle: string) => Promise<void>
}

/**
 * Estado e tratamento de erro de um download do painel.
 *
 * As quatro telas de exportação repetiam o mesmo `useState` + `setIsExporting(true)` + `.catch`
 * + `.finally` — e as de `ContestDetailPage` e `EventDetailPage` eram byte a byte a mesma coisa,
 * inclusive a frase fixa `'Não foi possível exportar os participantes.'` que **escondia** o
 * motivo real da recusa (ver `unwrapBlobError` em `helpers/index.ts`).
 *
 * Uma tela com dois downloads independentes usa duas instâncias: cada botão tem o seu estado,
 * que é o que já acontecia à mão no `ArtworkEvaluationPage`.
 */
export function useDownload(): IDownload {
  const toast = useAppToast()
  const [isDownloading, setIsDownloading] = useState(false)

  const download = useCallback(
    (run: () => Promise<unknown>, errorTitle: string) => {
      setIsDownloading(true)

      return run()
        .then(() => undefined)
        .catch((error) => toast.error(errorTitle, error))
        .finally(() => setIsDownloading(false))
    },
    [toast]
  )

  return { isDownloading, download }
}
